/**
 * 공통 포맷 유틸 — 날짜/시각/숫자/통화
 *
 * antd Table 컬럼의 `render` 에서 ISO 타임스탬프를 사람이 읽기 쉬운 형태로
 * 변환하기 위해 사용한다. 잘못된 값은 원본 문자열을 그대로 돌려주거나 빈
 * 문자열을 반환하여 렌더링 오류가 발생하지 않도록 한다.
 */
import dayjs from 'dayjs'

/**
 * ISO/Date 값을 지정한 패턴 문자열로 포맷.
 * @param v - 변환할 값 (Date / ISO 문자열 / number)
 * @param pattern - dayjs 포맷 패턴 (기본 `YYYY-MM-DD`)
 * @returns 포맷된 문자열, 잘못된 값이면 원본 문자열 또는 빈 문자열
 */
export function fmtDate(v: unknown, pattern = 'YYYY-MM-DD'): string {
  if (v == null || v === '') return ''
  const d = dayjs(v as dayjs.ConfigType)
  return d.isValid() ? d.format(pattern) : String(v)
}

/**
 * ISO/Date 값을 날짜+시각 패턴으로 포맷.
 * @param v - 변환할 값
 * @param pattern - dayjs 포맷 패턴 (기본 `YYYY-MM-DD HH:mm:ss`)
 * @returns 포맷된 문자열
 */
export function fmtDateTime(v: unknown, pattern = 'YYYY-MM-DD HH:mm:ss'): string {
  return fmtDate(v, pattern)
}

/**
 * 숫자를 천단위 구분 문자열로 포맷.
 * @param v - 변환할 값
 * @param fraction - 소수점 자리수 (기본 0)
 * @returns 포맷된 숫자 문자열, 숫자가 아니면 빈 문자열
 */
export function fmtNumber(v: unknown, fraction = 0): string {
  const n = Number(v)
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, {
        minimumFractionDigits: fraction,
        maximumFractionDigits: fraction
      })
    : ''
}

/**
 * 숫자를 원화(KRW) 통화 문자열로 포맷.
 * @param v - 변환할 값
 * @returns `₩` 접두 통화 문자열, 숫자가 아니면 `₩0`
 */
export function fmtCurrency(v: unknown): string {
  const n = Number(v)
  return Number.isFinite(n) ? '₩' + n.toLocaleString() : '₩0'
}
